import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';

type PreLoadFunc = { preload?: () => void };
type LazyRouteLoader<T extends ComponentType<any>> = (() => Promise<{ Component: T }>) & Required<PreLoadFunc>;

export const lazyRoute = <M extends object, T extends ComponentType<any>>(
  importPath: () => Promise<M>,
  select: (module: M) => T,
): LazyRouteLoader<T> => {
  const load = (async () => ({ Component: select(await importPath()) })) as LazyRouteLoader<T>;
  load.preload = () => {
    const run = () => void importPath().catch(() => {});
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run);
    } else {
      run();
    }
  };
  return load;
};

export type RouteObjectWithLazyPreload = RouteObject & {
  skipPreload?: boolean;
  lazy?: RouteObject['lazy'] & PreLoadFunc;
};

export const preloadRoutes = (routes: RouteObjectWithLazyPreload[]): void => {
  for (const route of routes) {
    if (!route.skipPreload) {
      route.lazy?.preload?.();
    }

    if (route.children) {
      preloadRoutes(route.children);
    }
  }
};
